import {
  BottomTabNavigationOptions,
  createBottomTabNavigator,
} from '@react-navigation/bottom-tabs';
import { defineMessages } from 'react-intl';
import { TouchableWithoutFeedback, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ellipse, Svg } from 'react-native-svg';

import { Icon } from '@oui/app-core/src/components/Icon';
import { Text } from '@oui/app-core/src/components/Text';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { useTheme } from '@oui/app-core/src/styles';
import { EatingLog } from '@oui/eating-log';

import { Home } from '@src/screens/Home';
import { Learn } from '@src/screens/Learn';
import { Practice } from '@src/screens/Practice';
import { UserProfile } from '@src/screens/UserProfile';
import type { HomeTabParamList } from '@src/types/navigation';

function TabLabel(props: { focused: boolean; color: string; text: string }) {
  return (
    <View
      style={{
        alignItems: 'center',
        paddingVertical: 2,
        marginTop: -4,
        width: '100%',
      }}
    >
      <Text
        text={props.text}
        color={props.color}
        size={13}
        weight="semibold"
        numberOfLines={1}
        textAlign="center"
      />
      <View
        style={{
          backgroundColor: props.focused ? props.color : 'transparent',
          height: 2,
          width: '70%',
        }}
      ></View>
    </View>
  );
}

const tabLabels = defineMessages({
  home: { id: 'Tabs_homeTab', defaultMessage: 'Home' },
  learn: { id: 'Tabs_learnTab', defaultMessage: 'Learn' },
  eatingLog: { id: 'Tabs_eatingLog', defaultMessage: 'Eating log' },
  practice: { id: 'Tabs_practiceTab', defaultMessage: 'Practice' },
  profile: { id: 'Tabs_profileTab', defaultMessage: 'Profile' },
});

const Tab = createBottomTabNavigator<HomeTabParamList>();
export const TabNavigator = () => {
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const { $t } = useI18n();

  const tabAccessibilityLabel = (values: { tabName: string; count: number }) => {
    return $t(
      { id: 'Tabs_tabAccessibilityLabel', defaultMessage: '{tabName}, {count} of {total}' },
      { ...values, total: 5 },
    ) as string;
  };

  return (
    <Tab.Navigator
      screenOptions={({ route }) => {
        const options: BottomTabNavigationOptions = {
          headerShown: false,
          tabBarStyle: {
            position: 'absolute',
            height: 60 + insets.bottom,
            backgroundColor: theme.color.accentTwo100,
          },
          tabBarBackground: () => {
            return (
              <Svg width="100%" height={40} viewBox="0 0 100 20" style={{ marginTop: -14 }}>
                <Ellipse cx="50" cy="10" rx="103" ry="10" fill={theme.color.accentTwo100} />
              </Svg>
            );
          },
          tabBarActiveTintColor: 'white',
          tabBarInactiveTintColor: theme.color.accentTwo300,
          // Adapted from https://github.com/react-navigation/react-navigation/blob/a484c1d7834f195ac0cfccda6a9f905218bc2274/packages/bottom-tabs/src/views/BottomTabItem.tsx#L156-L164
          // customized accessibilityRole
          tabBarButton: ({ children, style, onPress, to, accessibilityRole, ...rest }) => (
            <TouchableWithoutFeedback {...rest} accessibilityRole="tab" onPress={onPress}>
              <View style={style}>{children}</View>
            </TouchableWithoutFeedback>
          ),
          tabBarIcon: ({ color: tintColor }) => {
            const routeName = route.name;

            let icon = null;
            if (routeName === 'Home') {
              icon = <Icon color={tintColor} name="home" />;
            } else if (routeName === 'Learn') {
              icon = <Icon color={tintColor} name="sessions" />;
            } else if (routeName === 'Log') {
              icon = <Icon color={tintColor} name="eating-log" />;
            } else if (routeName === 'Practice') {
              icon = <Icon color={tintColor} name="practice" />;
            } else if (routeName === 'UserProfile') {
              icon = <Icon color={tintColor} name="person" />;
            }

            return <View style={{ paddingTop: 4 }}>{icon}</View>;
          },
          tabBarHideOnKeyboard: true,
        };
        return options;
      }}
      initialRouteName="Home"
    >
      <Tab.Screen
        name="Home"
        component={Home}
        options={{
          headerShown: false,
          tabBarLabel: (p) => <TabLabel {...p} text={$t(tabLabels.home)} />,
          tabBarTestID: 'NAV_Home',
          tabBarAccessibilityLabel: tabAccessibilityLabel({
            tabName: $t(tabLabels.home),
            count: 1,
          }),
        }}
      />
      <Tab.Screen
        name="Learn"
        component={Learn}
        options={{
          tabBarLabel: (p) => <TabLabel {...p} text={$t(tabLabels.learn)} />,
          tabBarTestID: 'NAV_Learn',
          tabBarAccessibilityLabel: tabAccessibilityLabel({
            tabName: $t(tabLabels.learn),
            count: 2,
          }),
        }}
      />
      <Tab.Screen
        name="Log"
        component={EatingLog}
        options={{
          tabBarLabel: (p) => <TabLabel {...p} text={$t(tabLabels.eatingLog)} />,
          tabBarTestID: 'NAV_Log',
          tabBarAccessibilityLabel: tabAccessibilityLabel({
            tabName: $t(tabLabels.eatingLog),
            count: 3,
          }),
        }}
      />
      <Tab.Screen
        name="Practice"
        component={Practice}
        options={{
          tabBarLabel: (p) => <TabLabel {...p} text={$t(tabLabels.practice)} />,
          tabBarTestID: 'NAV_Practice',
          tabBarAccessibilityLabel: tabAccessibilityLabel({
            tabName: $t(tabLabels.practice),
            count: 4,
          }),
        }}
      />
      <Tab.Screen
        name="UserProfile"
        component={UserProfile}
        options={{
          tabBarLabel: (p) => <TabLabel {...p} text={$t(tabLabels.profile)} />,
          tabBarTestID: 'NAV_UserProfile',
          tabBarAccessibilityLabel: tabAccessibilityLabel({
            tabName: $t(tabLabels.profile),
            count: 5,
          }),
        }}
      />
    </Tab.Navigator>
  );
};
